import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import type { ExpensesSummaryQuery } from "./client";
import { expensesApi } from "./client";

export const expenseKeys = {
  all: ["expenses"] as const,
  summary: (query: ExpensesSummaryQuery) =>
    ["expenses", "summary", query] as const,
  list: (query: OffsetPageQueryDTO) => ["expenses", "list", query] as const,
  detail: (id: string) => ["expenses", "detail", id] as const,
  categories: ["expenses", "categories"] as const,
  overviewCharts: ["expenses", "overview-charts"] as const,
};

function invalidateExpenses(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: expenseKeys.all });
}

export function useExpensesSummaryQuery(query: ExpensesSummaryQuery) {
  return useQuery({
    queryKey: expenseKeys.summary(query),
    queryFn: () => expensesApi.records.getSummary(query),
  });
}

export function useExpensesOverviewChartsQuery() {
  return useQuery({
    queryKey: expenseKeys.overviewCharts,
    queryFn: () => expensesApi.records.getOverviewCharts(),
  });
}

export function useExpenseRecordsQuery(query: OffsetPageQueryDTO) {
  return useQuery({
    queryKey: expenseKeys.list(query),
    queryFn: () => expensesApi.records.list(query),
  });
}

export function useExpenseRecordQuery(id: string | null) {
  return useQuery({
    queryKey: expenseKeys.detail(id ?? ""),
    queryFn: () => {
      if (!id) throw new Error("missing expense id");
      return expensesApi.records.getById(id);
    },
    enabled: !!id,
  });
}

export function useExpenseCategoriesQuery() {
  return useQuery({
    queryKey: expenseKeys.categories,
    queryFn: async () => {
      const res = await expensesApi.categories.list();
      return res.data;
    },
  });
}

export function useCreateExpenseRecordMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: expensesApi.records.create,
    onSuccess: () => {
      toast.success("ບັນທຶກຄ່າໃຊ້ຈ່າຍສຳເລັດ");
      invalidateExpenses(qc);
    },
  });
}

export function useUpdateExpenseRecordMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof expensesApi.records.update>[1]) =>
      expensesApi.records.update(id, data),
    onSuccess: () => {
      toast.success("ອັບເດດຄ່າໃຊ້ຈ່າຍສຳເລັດ");
      invalidateExpenses(qc);
    },
  });
}

export function useSubmitExpenseRecordMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => expensesApi.records.submit(id),
    onSuccess: () => {
      toast.success("ສົ່ງອະນຸມັດສຳເລັດ");
      invalidateExpenses(qc);
    },
  });
}

export function useApproveExpenseRecordMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => expensesApi.records.approve(id),
    onSuccess: () => {
      toast.success("ອະນຸມັດສຳເລັດ");
      invalidateExpenses(qc);
    },
  });
}

export function useRejectExpenseRecordMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { rejectionReason?: string }) =>
      expensesApi.records.reject(id, data),
    onSuccess: () => {
      toast.success("ປະຕິເສດສຳເລັດ");
      invalidateExpenses(qc);
    },
  });
}

export function useMarkExpensePaidMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      paymentMethod: "cash" | "bank_transfer" | "other";
      paidAt?: string;
      notes?: string;
    }) => expensesApi.records.markPaid(id, data),
    onSuccess: () => {
      toast.success("ບັນທຶກການຈ່າຍສຳເລັດ");
      invalidateExpenses(qc);
    },
  });
}

export function useCloseExpenseRecordMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => expensesApi.records.close(id),
    onSuccess: () => {
      toast.success("ປິດລາຍການສຳເລັດ");
      invalidateExpenses(qc);
    },
  });
}

export function useCancelExpenseRecordMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => expensesApi.records.cancel(id),
    onSuccess: () => {
      toast.success("ຍົກເລີກລາຍການສຳເລັດ");
      invalidateExpenses(qc);
    },
  });
}

export function useVoidExpenseRecordMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { voidReason: string }) =>
      expensesApi.records.void(id, data),
    onSuccess: () => {
      toast.success("ຍົກເລີກລາຍການສຳເລັດ");
      invalidateExpenses(qc);
    },
  });
}

export function useAddExpenseAttachmentMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      storageKey: string;
      fileName: string;
      contentType?: string;
    }) => expensesApi.records.addAttachment(id, data),
    onSuccess: () => {
      toast.success("ແນບໄຟລ໌ສຳເລັດ");
      invalidateExpenses(qc);
    },
  });
}

export function useDeleteExpenseAttachmentMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) =>
      expensesApi.records.deleteAttachment(id, attachmentId),
    onSuccess: () => {
      toast.success("ລຶບໄຟລ໌ສຳເລັດ");
      invalidateExpenses(qc);
    },
  });
}

export function useCreateExpenseCategoryMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: expensesApi.categories.create,
    onSuccess: () => {
      toast.success("ສ້າງຫມວດສຳເລັດ");
      qc.invalidateQueries({ queryKey: expenseKeys.categories });
    },
  });
}

export function useUpdateExpenseCategoryMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Parameters<typeof expensesApi.categories.update>[1];
    }) => expensesApi.categories.update(id, data),
    onSuccess: () => {
      toast.success("ອັບເດດຫມວດສຳເລັດ");
      qc.invalidateQueries({ queryKey: expenseKeys.categories });
    },
  });
}
